import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changePassword,
  fetchCanSetPassword,
  fetchLinkedAccounts,
  fetchMfaStatus,
  fetchSessions,
  revokeSession,
  setFirstPassword,
  unlinkSocialAccount,
  type ChangePasswordInput,
} from './api';

/**
 * Identity-shaped keys, not workspace-shaped ones.
 *
 * `lib/queryKeys.ts` documents a hard rule that every workspace-scoped key
 * starts `['ws', workspaceId, …]`, with `profile` as the one deliberate
 * exception — sessions, and MFA state belong to the signed-in person, not to
 * any one workspace, same as `profile`. That file is off-limits this round
 * (owned by another agent's edits), so these are declared locally here rather
 * than added there; reported to the lead in case `['account', …]` belongs
 * folded into `queryKeys` alongside `profile` once both rounds land.
 */
export const accountKeys = {
  sessions: () => ['account', 'sessions'] as const,
  mfaStatus: () => ['account', 'mfa-status'] as const,
  linkedAccounts: () => ['account', 'linked-accounts'] as const,
  canSetPassword: () => ['account', 'can-set-password'] as const,
};

export function useSessions() {
  return useQuery({
    queryKey: accountKeys.sessions(),
    queryFn: () => fetchSessions(),
  });
}

export function useMfaStatus() {
  return useQuery({
    queryKey: accountKeys.mfaStatus(),
    queryFn: () => fetchMfaStatus(),
  });
}

/**
 * Revoking a session hands back `wasCurrentSession` — this hook only refetches
 * the list when that is false. When it is true the list is about to describe a
 * session that no longer exists, and the caller (SessionsSection) owns ending
 * the local session the same deliberate way ChangePasswordSection does; see its
 * comment for why that is not folded in here.
 */
export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => revokeSession(sessionId),
    onSuccess: async (result) => {
      if (!result.wasCurrentSession) {
        await queryClient.invalidateQueries({ queryKey: accountKeys.sessions() });
      }
    },
  });
}

/**
 * Deliberately no `onSuccess` cache write here — see ChangePasswordSection for
 * what has to happen after a successful change (force sign-out, not a cache
 * update), which is a navigation concern that belongs in the component, not in
 * this data hook.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => changePassword(input),
  });
}

export function useLinkedAccounts() {
  return useQuery({
    queryKey: accountKeys.linkedAccounts(),
    queryFn: () => fetchLinkedAccounts(),
  });
}

/**
 * Whether this account has no password yet. Two screens ask: Password, to
 * decide between setting a first one and changing an existing one, and Sign-in
 * methods, to decide whether unlinking Google would leave no way in at all.
 * One query key, so they cannot disagree.
 */
export function useCanSetPassword() {
  return useQuery({
    queryKey: accountKeys.canSetPassword(),
    queryFn: () => fetchCanSetPassword(),
  });
}

export function useUnlinkSocialAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (provider: string) => unlinkSocialAccount(provider),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.linkedAccounts() }),
  });
}

/**
 * Same deliberate absence of an `onSuccess` as `useChangePassword`, for the
 * same reason: nauth revokes every session on a password being set, so what
 * follows is a sign-out, not a cache update. The component owns that.
 */
export function useSetFirstPassword() {
  return useMutation({
    mutationFn: (newPassword: string) => setFirstPassword(newPassword),
  });
}
